import { useCallback, useMemo } from 'react'
import {
    VLPOption,
    SelectedOption,
    VLPVariant,
    getOptionsFromVariant,
    findVariantByOptions,
    findByShopifyId,
    buildOptionKeyMap,
} from '@storefronts/common-types'

interface UseVariantSelectionProps {
    variants: VLPVariant[]
    options: VLPOption[]
    defaultVariantId?: string | null
    onVariantSelect: (variantId: string) => void
}

interface UseVariantSelectionReturn {
    selectedOptions: SelectedOption[]
    handleOptionSelect: (optionName: string, value: string) => void
}

export const useVariantSelection = ({
    variants,
    options,
    defaultVariantId,
    onVariantSelect,
}: UseVariantSelectionProps): UseVariantSelectionReturn => {
    const optionKeyMap = useMemo(() => buildOptionKeyMap(options), [options])

    const selectedOptions = useMemo<SelectedOption[]>(() => {
        const variant = defaultVariantId
            ? findByShopifyId(variants, defaultVariantId)
            : variants[0]
        return variant
            ? getOptionsFromVariant(variant, options, optionKeyMap)
            : []
    }, [defaultVariantId, variants, options, optionKeyMap])

    const handleOptionSelect = useCallback(
        (optionName: string, value: string) => {
            const updatedOptions = [
                ...selectedOptions.filter((opt) => opt.name !== optionName),
                { name: optionName, value },
            ].sort(
                (a, b) =>
                    options.findIndex((o) => o.name === a.name) -
                    options.findIndex((o) => o.name === b.name),
            )

            const matchingVariant = findVariantByOptions(
                variants,
                updatedOptions,
                optionKeyMap,
            )
            if (!matchingVariant) return

            onVariantSelect(matchingVariant.shopifyId)
        },
        [selectedOptions, options, variants, onVariantSelect, optionKeyMap],
    )

    return { selectedOptions, handleOptionSelect }
}
